import React from 'react';
import { string, number, shape, oneOf, oneOfType } from 'prop-types';
import LazyImage from '../LazyImage';

/*
 * Useful if you only need to retrieve the generated url with params
 */
export const getUrl = (baseUrl, newParams) => {
  // Check if no url was provided
  if (!baseUrl) return baseUrl;

  let url = baseUrl;
  // Declare an empty string for query params
  let query = '';
  // Split the url on '?'
  const splitted = url.split('?');
  // Declare an empty object to store params by key
  let params = {};

  // The base url will be the first part (no params)
  url = splitted[0];
  // If there are any params, store them in the params Object
  if (splitted.length > 1) {
    splitted[1].split('&').forEach(param => {
      const temp = param.split('=');

      params[temp[0]] = temp[1];
    });
  }
  // Add or replace with provided params
  params = {
    ...params,
    ...newParams,
    fit: params.fit || 'crop',
  };
  // Go through every param (if any) to build the query params
  if (Object.keys(params).length > 0) {
    Object.keys(params).forEach(key => {
      query = query.concat(`${key}=${params[key]}&`);
    });
    // Remove last unnecessary '&'
    query = query.slice(0, query.length - 1);
  }
  // Concat the query params to the url if any
  if (query.length > 0) url = `${url}?${query}`;
  // Return the url built
  return url;
};

/*
 * Useful to build an image url with custom query params.
 * You can render either a LazyImage or an img tag.
 */
const Image = ({ src, alt, lazyUrl, params, children, ...other }) => {
  // The url can be taken either from lazyUrl or src prop
  let url = lazyUrl || src;

  url = getUrl(url, params);
  if (lazyUrl) {
    return (
      <LazyImage lazyUrl={url} {...other}>
        {children}
      </LazyImage>
    );
  }

  return <img alt={alt} src={url} {...other} />;
};

Image.propTypes = {
  // If lazyUrl provided, a LazyImage will be rendered
  lazyUrl: string,
  // Img src, only used if no lazyUrl was provided
  src: string,
  // Alt for img tag, only used if no lazyUrl was provided
  alt: string,
  // Query params applied to get the optimized image url
  params: shape({
    // The fit parameter controls how the output image is fit to its target dimensions after resizing
    // API Reference: https://docs.imgix.com/apis/url/size/fit
    fit: oneOf([
      'clamp',
      'clip',
      'crop',
      'facearea',
      'fill',
      'fillmax',
      'max',
      'min',
      'scale',
    ]),
    // Crop mode controls how the image is aligned when fit=crop is set.
    // API Reference: https://docs.imgix.com/apis/url/size/crop
    crop: oneOf(['faces']),
    // The output format to convert the image to
    // API Reference: https://docs.imgix.com/apis/url/format/fm
    fm: oneOf([
      'gif',
      'jp2',
      'jpg',
      'json',
      'jxr',
      'mp4',
      'png',
      'png8',
      'png32',
      'pjpg',
      'webp',
    ]),
    // Custom height
    h: oneOfType([string, number]),
    // Custom width
    w: oneOfType([string, number]),
    /*
     * Other params not documented here can also be applied
     * API Reference: https://docs.imgix.com/apis/url
     */
  }),
};

Image.defaultProps = {
  lazyUrl: undefined,
  src: '',
  alt: '',
  params: {
    fit: 'crop',
  },
};

export default Image;
